import clsx from "clsx";
import Image from "next/image";
import Link from "next/link";
import {
  MdArrowOutward,
  MdClose,
  MdFavoriteBorder,
  MdKeyboardArrowDown,
  MdKeyboardArrowUp,
  MdOutlinePerson,
  MdOutlineShoppingCart,
  MdSearch,
} from "react-icons/md";

import remixImage from "public/images/remix.png";
import { useShoppingCart } from "@/hooks/useShoppingCart";
import { useAuthStore } from "@/stores/use-auth-store";
import { useUserStore } from "@/stores/use-user-store";

import { HEADER_SECTIONS, type ActiveFlyout } from "./Sections";
import { UserDropdown } from "./UserActionButtons/UserDropdown";

import type { JSX } from "react";

interface Props {
  activeFlyout: ActiveFlyout;
  onFlyoutChange: (flyout: ActiveFlyout) => void;
  searchTerm: string;
  onSearchTermChange: (term: string) => void;
  onSearch: () => void;
}

const iconButtonClassName = "flex h-5 w-5 items-center justify-center rounded-full";

/**
 * Barra de navegação desktop: logo, seções (abrem no hover), busca expansível, ícones e CTA.
 * O estado `activeFlyout` vive no `Header`; aqui só dispara mudanças.
 */
export const DesktopNav = ({
  activeFlyout,
  onFlyoutChange,
  searchTerm,
  onSearchTermChange,
  onSearch,
}: Props): JSX.Element => {
  const { activeUser } = useUserStore();
  const { allCartItems } = useShoppingCart({ skipPreCheckout: true });
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const cartItemsCount = allCartItems.length;
  const cartItemsLabel = `${cartItemsCount} ${cartItemsCount === 1 ? "item" : "itens"} no carrinho`;
  const isSearching = activeFlyout === "busca";

  /** Fecha uma seção aberta ao passar o mouse por itens sem submenu (não interrompe a busca). */
  const closeSectionFlyout = () => {
    if (!isSearching) {
      onFlyoutChange(null);
    }
  };

  return (
    <div className="w-full max-w-page-max-width items-center justify-between gap-3 pb-2 flex">
      <Link href="/" className="shrink-0">
        <Image src={remixImage} alt="Remix" className="h-auto w-[10rem]" sizes="136px" priority />
      </Link>

      {isSearching ? (
        <div className="relative flex max-w-[40rem] grow items-center rounded-full bg-base-input-opacity-50">
          <MdSearch size="1.25rem" className="pointer-events-none absolute left-2 text-brand-primary" />

          <input
            autoFocus
            value={searchTerm}
            onChange={(event) => onSearchTermChange(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                onSearch();
              }

              if (event.key === "Escape") {
                onFlyoutChange(null);
              }
            }}
            placeholder="Busque por estampas, temas ou produtos"
            aria-label="Buscar"
            className="h-5 w-full rounded-full bg-transparent pl-5 pr-5 outline-none"
          />

          <button
            type="button"
            aria-label="Fechar busca"
            className="absolute right-1 flex h-3 w-3 items-center justify-center rounded-full bg-base-input"
            onClick={() => {
              onSearchTermChange("");
              onFlyoutChange(null);
            }}
          >
            <MdClose size="1rem" />
          </button>
        </div>
      ) : (
        <nav className="flex items-center gap-1" aria-label="Seções">
          {HEADER_SECTIONS.map((section) => {
            const isActive = section.key === activeFlyout;

            return (
              <button
                key={section.key}
                type="button"
                aria-expanded={isActive}
                onMouseEnter={() => onFlyoutChange(section.key)}
                onFocus={() => onFlyoutChange(section.key)}
                onClick={() => onFlyoutChange(isActive ? null : section.key)}
                className={clsx(
                  "flex items-center rounded-full px-2 py-1 text-base font-bold",
                  isActive && "bg-base-input-opacity-50",
                )}
              >
                {section.name}
                {isActive ? <MdKeyboardArrowUp size="1rem" /> : <MdKeyboardArrowDown size="1rem" />}
              </button>
            );
          })}

          <Link
            href="/customizar-produto"
            onMouseEnter={closeSectionFlyout}
            className="rounded-full px-2 py-1 text-base font-bold text-brand-accent"
          >
            Customizar
          </Link>
        </nav>
      )}

      <div className="flex shrink-0 items-center gap-2" onMouseEnter={closeSectionFlyout}>
        <button
          type="button"
          aria-label="Buscar"
          className={clsx(iconButtonClassName, isSearching && "bg-base-input-opacity-50")}
          onClick={() => (isSearching ? onSearch() : onFlyoutChange("busca"))}
        >
          <MdSearch size="1.5rem" />
        </button>

        <Link href="/meus-desejos" aria-label="Meus desejos" className={iconButtonClassName}>
          <MdFavoriteBorder size="1.5rem" />
        </Link>

        <Link href="/meu-carrinho" aria-label={cartItemsLabel} className={iconButtonClassName}>
          <span className="relative flex h-full w-full items-center justify-center">
            <MdOutlineShoppingCart size="1.5rem" />

            {cartItemsCount > 0 && (
              <span
                aria-hidden="true"
                className="absolute -right-1 -top-1 flex h-[18px] min-w-[18px] items-center justify-center rounded-full border-2 border-white bg-red-500 px-1 text-[10px] font-bold leading-none text-white shadow-sm"
              >
                {cartItemsCount > 99 ? "99+" : cartItemsCount}
              </span>
            )}
          </span>
        </Link>

        {isAuthenticated && activeUser ? (
          <UserDropdown userName={activeUser.name} userRole={activeUser.role} />
        ) : (
          <Link href="/login" aria-label="Entrar" className={iconButtonClassName}>
            <MdOutlinePerson size="1.5rem" />
          </Link>
        )}

        <Link
          href="/associacao"
          id="desktop-a-sell-on-remix"
          className="flex h-5 items-center gap-0.5 rounded-full bg-brand-primary px-3 text-sm font-bold text-base-white"
        >
          Vender no Remix
          <MdArrowOutward size="0.875rem" className="text-brand-accent" />
        </Link>
      </div>
    </div>
  );
};
